import React, { useState, useRef, useLayoutEffect } from "react";
import { Image, FileText, Play, MapPin, X, Pencil, Eye } from "lucide-react";
import allOptionsIcon from "../../assets/alloptions.png";
import { TC_RADIUS } from "../../constants/templateCreatorConstants";
import WhatsAppFormattedText from "../whatsapp/WhatsAppFormattedText";
import {
  TemplateBubbleButtonRow,
  TemplateButtonIcon,
  getTemplateButtonLabel,
} from "../whatsapp/TemplateBubbleButtons";

const WA_GREEN = "#008069";
const SHEET_ICON = "#54656f";

const wallpaper =
  "bg-[#e5ddd5] bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23d3cec4%22%20fill-opacity%3D%220.15%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')]";

/** Tint a monochrome PNG icon to any color via CSS mask */
const TintedIcon = ({ src, color = WA_GREEN, className = "w-4 h-4" }) => (
  <span
    className={`${className} shrink-0 inline-block`}
    style={{
      backgroundColor: color,
      WebkitMaskImage: `url(${src})`,
      maskImage: `url(${src})`,
      WebkitMaskSize: "contain",
      maskSize: "contain",
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskPosition: "center",
      maskPosition: "center",
    }}
    aria-hidden="true"
  />
);

const PreviewButtonIcon = ({ type, color = WA_GREEN }) => (
  <TemplateButtonIcon type={type} color={color} />
);

/** Inline variable — auto width, highlight only (feels like editing the template). */
const EditableVarInput = ({
  value,
  placeholder,
  error,
  onChange,
  fullWidth = false,
}) => {
  const mirrorRef = useRef(null);
  const inputRef = useRef(null);
  const mirrorText = value || placeholder || " ";

  useLayoutEffect(() => {
    if (fullWidth || !mirrorRef.current || !inputRef.current) return;
    const w = mirrorRef.current.offsetWidth;
    inputRef.current.style.width = `${Math.max(w, 14)}px`;
  }, [mirrorText, fullWidth]);

  const highlightClass = error
    ? "bg-red-100 text-red-900 ring-1 ring-red-300"
    : "bg-[#eefa94] text-[#111b21] ring-1 ring-[#d4e86a]/80";

  if (fullWidth) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full block px-1.5 py-0.5 text-[12px] leading-snug rounded-sm border-0 outline-none ${highlightClass} focus:ring-2 focus:ring-[#c5d663]`}
        aria-label={`Variable ${placeholder}`}
      />
    );
  }

  return (
    <span className="inline align-baseline mx-px max-w-full relative">
      <span
        ref={mirrorRef}
        className="absolute left-0 top-0 invisible whitespace-pre pointer-events-none text-[12px] leading-snug px-0.5"
        aria-hidden="true"
      >
        {mirrorText}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`inline-block max-w-full px-0.5 py-0 border-0 rounded-sm text-[12px] leading-snug font-inherit align-baseline outline-none ${highlightClass} focus:ring-2 focus:ring-[#c5d663]`}
        style={{ width: 14 }}
        aria-label={`Variable ${placeholder}`}
      />
    </span>
  );
};

const AllOptionsSheet = ({ buttons, onClose }) => (
  <div className="absolute inset-0 z-20 flex flex-col justify-end rounded-b-[3px] overflow-hidden">
    <button
      type="button"
      className="absolute inset-0 bg-black/25"
      onClick={onClose}
      aria-label="Close all options"
    />
    <div className="relative bg-white rounded-t-[12px] shadow-[0_-4px_24px_rgba(0,0,0,0.15)] max-h-[85%] flex flex-col">
      <div className="flex justify-center pt-2.5 pb-1 shrink-0">
        <div className="w-9 h-1 bg-[#ccd0d5] rounded-full" />
      </div>

      <div className="flex items-center px-4 py-2.5 border-b border-[#e9edef] shrink-0 relative min-h-[44px]">
        <button
          type="button"
          onClick={onClose}
          className="absolute left-3 p-1.5 text-[#65676b] hover:bg-[#f0f2f5] rounded-full transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <h4 className="flex-1 text-center text-[16px] font-semibold text-[#050505]">
          All options
        </h4>
      </div>

      <div className="overflow-y-auto">
        {buttons.map((btn, i) => {
          const prev = buttons[i - 1];
          const showDivider =
            i > 0 &&
            prev?.type === "QUICK_REPLY" &&
            btn.type !== "QUICK_REPLY";
          return (
            <React.Fragment key={btn.id || i}>
              {showDivider && <div className="border-t border-[#e9edef]" />}
              <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#e9edef] last:border-b-0">
                <PreviewButtonIcon type={btn.type} color={SHEET_ICON} />
                <span className="text-[15px] text-[#050505] leading-snug">
                  {getTemplateButtonLabel(btn)}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  </div>
);

const MessageBubble = ({
  children,
  buttonsBlock,
  showAllOptionsRow,
  onSeeAllOptions,
}) => (
  <div className={`${wallpaper} p-3`}>
    <div className="bg-white shadow-sm overflow-hidden rounded-[4px]">
      {children}
      {buttonsBlock}
      {showAllOptionsRow && (
        <div className="border-t border-[#e9edef]">
          <button
            type="button"
            onClick={onSeeAllOptions}
            className="w-full py-2.5 text-[13px] text-[#008069] font-normal text-center flex items-center justify-center gap-2 hover:bg-[#f5f6f6] transition-colors"
          >
            <TintedIcon src={allOptionsIcon} />
            See all options
          </button>
        </div>
      )}
    </div>
  </div>
);

const TemplatePreview = ({
  state,
  editable = false,
  variableValues = {},
  variableErrors = {},
  onVariableChange,
  variableKeyResolver,
  viewMode = "edit",
  onViewModeChange,
}) => {
  const [showAllOptions, setShowAllOptions] = useState(false);

  const resolveVarKey = (component, varName) => {
    if (variableKeyResolver) return variableKeyResolver(component, varName);
    return component === "HEADER" ? `header_${varName}` : `body_${varName}`;
  };

  const {
    category,
    headerText,
    headerVariables,
    body,
    bodyVariables,
    footer,
    ctaButtons,
    quickReplyButtons,
    mediaSample,
    variableType,
    addSecurityRecommendation,
    addExpiryTime,
    expiryMinutes,
    headerFilePreview,
  } = state;

  const replaceVars = (text, variables) => {
    if (!text) return "";
    let result = text;
    variables.forEach((v) => {
      const regex = new RegExp(`\\{\\{${v.name}\\}\\}`, "g");
      result = result.replace(
        regex,
        v.sample ||
          (variableType === "named" ? `[${v.name}]` : `[{{${v.name}}}]`)
      );
    });
    return result;
  };

  const renderFormatting = (text) => {
    if (!text) return null;
    return <WhatsAppFormattedText text={text} />;
  };

  /** Split template text into static chunks and variable slots for inline editing. */
  const renderEditableText = (text, component, className = "") => {
    if (!text) return null;

    const segments = [];
    const regex = /\{\{([^}]+)\}\}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          type: "text",
          content: text.slice(lastIndex, match.index),
        });
      }
      segments.push({
        type: "var",
        varName: String(match[1] || "").trim(),
        raw: match[0],
      });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      segments.push({ type: "text", content: text.slice(lastIndex) });
    }
    if (segments.length === 0) {
      segments.push({ type: "text", content: text });
    }

    return (
      <span className={className}>
        {segments.map((seg, i) => {
          if (seg.type === "text") {
            return <span key={i}>{renderFormatting(seg.content)}</span>;
          }
          const key = resolveVarKey(component, seg.varName);
          return (
            <EditableVarInput
              key={i}
              value={variableValues[key] ?? ""}
              placeholder={seg.raw}
              error={variableErrors[key]}
              onChange={(v) => onVariableChange?.(key, v)}
            />
          );
        })}
      </span>
    );
  };

  const allButtons = [
    ...quickReplyButtons.map((b) => ({
      ...b,
      type: "QUICK_REPLY",
      section: "qr",
    })),
    ...ctaButtons.map((b) => ({ ...b, section: "cta" })),
  ];

  const now = new Date();
  const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  const hasMore = allButtons.length > 3;
  const bubbleButtons = hasMore ? allButtons.slice(0, 2) : allButtons;

  const hasContent =
    body ||
    headerText ||
    footer ||
    allButtons.length > 0 ||
    mediaSample !== "none";

  const openAllOptions = () => setShowAllOptions(true);
  const closeAllOptions = () => setShowAllOptions(false);

  const renderMediaHeader = () => {
    if (mediaSample === "none") return null;

    if (mediaSample === "image") {
      return headerFilePreview ? (
        <img
          src={headerFilePreview}
          alt="Header"
          className="w-full h-36 object-cover"
        />
      ) : (
        <div className="w-full h-36 bg-gradient-to-br from-gray-200 to-gray-300 flex flex-col items-center justify-center">
          <Image size={28} className="text-gray-400 mb-1" />
          <span className="text-[10px] text-gray-400">Image</span>
        </div>
      );
    }
    if (mediaSample === "video") {
      return (
        <div className="w-full h-36 bg-gradient-to-br from-gray-800 to-gray-900 flex flex-col items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mb-1">
            <Play size={18} className="text-white ml-0.5" />
          </div>
          <span className="text-[10px] text-gray-400">Video</span>
        </div>
      );
    }
    if (mediaSample === "document") {
      return (
        <div className="w-full bg-[#f0f2f5] px-3 py-3 flex items-center gap-2.5 border-b border-gray-200">
          <div className="w-8 h-10 bg-red-500 flex items-center justify-center shrink-0">
            <FileText size={16} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-gray-800 truncate">
              Document.pdf
            </p>
            <p className="text-[9px] text-gray-500">PDF</p>
          </div>
        </div>
      );
    }
    if (mediaSample === "location") {
      return (
        <div className="w-full h-28 bg-gradient-to-br from-green-50 to-blue-50 flex flex-col items-center justify-center">
          <MapPin size={24} className="text-red-500 mb-1" />
          <span className="text-[10px] text-gray-500">Location</span>
        </div>
      );
    }
    return null;
  };

  const renderBubbleContent = () => {
    if (category === "AUTHENTICATION") {
      return (
        <MessageBubble
          buttonsBlock={
            <div className="border-t border-[#e9edef]">
              <TemplateBubbleButtonRow btn={{ type: "COPY_CODE", text: "Copy code" }} />
            </div>
          }
        >
          <div className="px-2.5 py-2">
            <p className="text-[12px] text-gray-800 leading-relaxed">
              <strong>123456</strong> is your verification code.
              {addSecurityRecommendation &&
                " For your security, do not share this code."}
            </p>
            {addExpiryTime && expiryMinutes && (
              <p className="text-[10px] text-gray-500 mt-1">
                This code expires in {expiryMinutes} minutes.
              </p>
            )}
            <span className="text-[9px] text-gray-400 float-right mt-0.5">
              {timeStr}
            </span>
          </div>
        </MessageBubble>
      );
    }

    if (!hasContent) {
      return (
        <div
          className={`${wallpaper} p-3 min-h-[140px] flex items-center justify-center`}
        >
          <p className="text-[11px] text-gray-500 italic">
            Start adding content to see preview
          </p>
        </div>
      );
    }

    const previewHeader = editable ? null : replaceVars(headerText, headerVariables);
    const previewBody = editable ? null : replaceVars(body, bodyVariables);

    const editableCtaButtons = editable
      ? ctaButtons.map((btn, index) => {
          if (btn.url_type !== "dynamic") return btn;
          const key = variableKeyResolver
            ? variableKeyResolver("BUTTON", String(index))
            : `button_text_${index}`;
          return { ...btn, _editKey: key };
        })
      : [];

    return (
      <MessageBubble
        showAllOptionsRow={hasMore}
        onSeeAllOptions={openAllOptions}
        buttonsBlock={
          bubbleButtons.length > 0 ? (
            <div className="border-t border-[#e9edef] px-2 py-2 flex flex-col gap-1.5">
              {bubbleButtons.map((btn, i) => (
                <TemplateBubbleButtonRow
                  key={btn.id || i}
                  btn={btn}
                  onClick={hasMore ? openAllOptions : undefined}
                  variant="preview"
                />
              ))}
              {editable &&
                editableCtaButtons
                  .filter((b) => b._editKey)
                  .map((btn) => (
                    <div
                      key={btn._editKey}
                      className="px-2.5 py-1.5 border-t border-[#e9edef] flex items-center justify-center gap-1 flex-wrap text-[11px] text-gray-500"
                    >
                      <span className="shrink-0">{btn.text} link:</span>
                      <EditableVarInput
                        value={variableValues[btn._editKey] ?? ""}
                        placeholder="value"
                        error={variableErrors[btn._editKey]}
                        onChange={(v) => onVariableChange?.(btn._editKey, v)}
                      />
                    </div>
                  ))}
            </div>
          ) : null
        }
      >
        {renderMediaHeader()}
        <div className="px-2.5 py-2">
          {mediaSample === "none" && headerText && (
            <p className="text-[12px] font-bold text-gray-900 mb-0.5 leading-snug">
              {editable
                ? renderEditableText(headerText, "HEADER")
                : renderFormatting(previewHeader)}
            </p>
          )}
          {body && (
            <div className="text-[12px] text-gray-800 leading-relaxed">
              {editable
                ? renderEditableText(body, "BODY")
                : renderFormatting(previewBody)}
            </div>
          )}
          {footer && (
            <p className="text-[10px] text-gray-500 mt-1.5">
              <WhatsAppFormattedText text={footer} />
            </p>
          )}
          <div className="flex justify-end mt-0.5">
            <span className="text-[9px] text-gray-400">{timeStr}</span>
          </div>
        </div>
      </MessageBubble>
    );
  };

  return (
    <div className={`border border-[#dddfe2] ${TC_RADIUS} bg-white overflow-hidden`}>
      <div className="px-4 py-3 border-b border-[#dddfe2] flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-[#050505]">
            Template preview
          </h3>
          {editable && viewMode === "edit" && (
            <p className="text-[11px] text-gray-500 mt-0.5">
              Tap highlighted text to edit variable values
            </p>
          )}
          {editable && viewMode === "preview" && (
            <p className="text-[11px] text-gray-500 mt-0.5">
              Final message with your values applied
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onViewModeChange && (
            <>
              <button
                type="button"
                onClick={() => onViewModeChange("edit")}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
                  viewMode === "edit"
                    ? "bg-[#eefa94] text-[#17181b]"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Pencil size={12} />
                Edit
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange("preview")}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
                  viewMode === "preview"
                    ? "bg-[#eefa94] text-[#17181b]"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Eye size={12} />
                Preview
              </button>
            </>
          )}
          {hasMore && (
            <button
              type="button"
              onClick={openAllOptions}
              className={`p-1 border border-[#ccd0d5] bg-white hover:bg-[#f0f2f5] transition-colors shrink-0 ${TC_RADIUS}`}
              title="Preview all button options"
              aria-label="Preview all button options"
            >
              <Play size={16} className="text-[#65676b] fill-[#65676b]" />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 relative min-h-[160px]">
        {renderBubbleContent()}
        {showAllOptions && hasMore && (
          <AllOptionsSheet buttons={allButtons} onClose={closeAllOptions} />
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
